import { useCurrentTime } from '@plato/ui-legacy/lib/hooks/use-current-time';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWakeLock } from './use-wake-lock.ts';

async function getMicrophone(): Promise<MediaRecorder> {
    const userMedia = await navigator.mediaDevices.getUserMedia({
        audio: true,
    });

    return new MediaRecorder(userMedia);
}

export function useMicrophone(onComplete: (data: Blob, duration: number) => void) {
    const [recordingStartedAt, setRecordingStartedAt] = useState<number>();
    const currentTime = useCurrentTime();

    const isRecording = !!recordingStartedAt;

    useWakeLock(isRecording);

    const microphoneRef = useRef<MediaRecorder | null>(null);

    const timeSinceStarted = useMemo(() => {
        if (!recordingStartedAt) {
            return 0;
        }

        return Math.max(currentTime.getTime() - recordingStartedAt, 0);
    }, [recordingStartedAt, currentTime]);

    const handleComplete = (blob: Blob) => {
        onComplete(blob, timeSinceStarted);
    };

    const onCompleteRef = useRef(handleComplete);

    useLayoutEffect(() => {
        onCompleteRef.current = handleComplete;
    }, [handleComplete]);

    const startRecording = useCallback(async () => {
        if (microphoneRef.current) {
            return null;
        }

        const microphone = await getMicrophone();

        microphoneRef.current = microphone;

        microphone.onstart = () => {
            console.log('client: microphone opened');
            setRecordingStartedAt(Date.now());
        };

        microphone.onstop = () => {
            console.log('client: microphone closed');
            setRecordingStartedAt(undefined);
            microphoneRef.current = null;
        };

        microphone.ondataavailable = (e) => {
            const data = e.data;

            onCompleteRef.current(data);
        };

        microphone.start();
    }, []);

    useEffect(() => {
        return () => {
            microphoneRef.current?.stop();
        };
    }, []);

    const stopRecording = useCallback(() => {
        const recorder = microphoneRef.current;
        const stream = recorder?.stream;

        recorder?.stop();

        for (const track of stream?.getTracks() ?? []) {
            track.stop();
        }
    }, []);

    return {
        timeSinceStarted,
        isRecording,
        startRecording,
        stopRecording,
    };
}
